/* solenne — shared front-end engine.
   Feature-detects what's on the page: palette engine, stars, drowsy type,
   reveals, menu, magnetic buttons, home swatch follower, PDP buy logic. */
(() => {
  const lerp = (a, b, t) => a + (b - a) * t;
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = matchMedia('(pointer: fine)').matches;
  const isMobile = !fine || innerWidth < 760;   // phones: trim the expensive flourishes

  /* ---------- money ---------- */
  const currency = (window.theme && window.theme.moneyCurrency) || 'USD';
  const money = (cents) => {
    const s = new Intl.NumberFormat('en-US', {
      style: 'currency', currency, minimumFractionDigits: cents % 100 ? 2 : 0
    }).format(cents / 100);
    return s.includes(currency) ? s : s + ' ' + currency;   // show ISO code once (e.g. "A$226 AUD"), never double
  };
  window.solMoney = money;
  /* append the presentment currency code to static (Liquid-rendered) prices so "$226" reads unambiguously as "$226 AUD".
     Converted-currency prices lose the .money wrapper, so match any leaf whose whole text is a bare amount. */
  const AMT = /^\s*[$€£¥]\s?\d[\d,]*(\.\d{1,2})?\s*$/;   // symbol-led amount only — never a quantity or prose
  const tagStaticMoney = () => {
    if (!currency) return;
    document.querySelectorAll('body *').forEach((el) => {
      if (el.children.length || el.closest('.bag-drawer, [data-money-done]')) return;
      let t = el.textContent;
      if (!AMT.test(t)) return;
      t = t.trim();
      if (currency !== 'USD') t = t.replace(/\s*USD\s*$/, '');
      el.textContent = t.includes(currency) ? t : t + ' ' + currency;
      el.setAttribute('data-money-done', '');
    });
  };
  if (document.readyState !== 'loading') tagStaticMoney();
  else document.addEventListener('DOMContentLoaded', tagStaticMoney);

  /* ---------- toast ---------- */
  const toast = document.getElementById('toast');
  let toastTimer;
  window.solToast = (html) => {
    if (!toast) return;
    toast.innerHTML = html;
    toast.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('show'), 2600);
  };

  /* ---------- intro curtain: the room is dark, the clock ticks to 03:00, the lights come up ---------- */
  const fireLifted = () => {
    window.__solIntroLifted = true;
    dispatchEvent(new CustomEvent('sol:introlift'));
  };
  const intro = document.getElementById('intro');
  if (intro) {
    const root = document.documentElement;
    let seen = false;
    try { seen = sessionStorage.getItem('sol_introSeen') === '1'; } catch (e) {}
    const lift = () => {
      intro.classList.add('lift');
      root.classList.remove('intro-on');
      fireLifted();
      const done = () => { intro.remove(); };
      intro.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 1400); // fallback if transitionend never fires
    };
    if (reduced || seen) {
      // only play the curtain once per session — returning/navigating customers skip it
      intro.remove();
      fireLifted();
    } else {
      try { sessionStorage.setItem('sol_introSeen', '1'); } catch (e) {}
      root.classList.add('intro-on');
      const hasUtm = /utm_|fbclid|gclid|ttclid|epik|_branch/i.test(location.search);  // any ad click-id skips the curtain → best LCP for paid
      const hold = document.visibilityState === 'hidden' || hasUtm ? 0 : (isMobile ? 400 : 1100);

      /* climb the clock 21:00 → 03:00 and fill the progress line while we hold */
      const clockEl = document.getElementById('introClock');
      const barEl = document.getElementById('introBar');
      if (hold > 0 && (clockEl || barEl)) {
        const START = 21 * 60, END = 27 * 60;            // 21:00 → 03:00 (next day)
        const t0 = performance.now(), runFor = hold - 250;
        (function climb(now) {
          const p = clamp((now - t0) / runFor, 0, 1);
          const eased = 1 - Math.pow(1 - p, 3);          // easeOutCubic
          const m = Math.round(lerp(START, END, eased)) % 1440;
          if (clockEl) {
            clockEl.textContent = `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
            if (p >= 1) clockEl.classList.add('struck');
          }
          if (barEl) barEl.style.transform = 'scaleX(' + eased.toFixed(3) + ')';
          if (p < 1) requestAnimationFrame(climb);
        })(t0);
      }

      // lift once the curtain has finished waking; bail early if the tab was hidden
      setTimeout(lift, hold);
    }
  } else {
    fireLifted();
  }

  /* ---------- images develop as they load (catch cached + guarantee visibility) ---------- */
  const markLoaded = (img) => img.classList.add('loaded');
  const wireImg = (img) => {
    if (img.complete && img.naturalWidth > 0) return markLoaded(img);
    img.addEventListener('load', () => markLoaded(img), { once: true });
    img.addEventListener('error', () => markLoaded(img), { once: true }); // onerror swaps to raw asset
  };
  document.querySelectorAll('img.imgfade').forEach(wireImg);
  // backstop: nothing stays invisible even if a load event is missed
  setTimeout(() => document.querySelectorAll('img.imgfade:not(.loaded)').forEach(markLoaded), 5000);

  /* 2026-09-05: the PALETTES object (a scroll-position colour-interpolation engine, cream
     to near-black) and everything it drove are deleted. It was already dead: solenne.js
     loads only on theme.liquid pages (blog/policy/collection/etc — layout/artifact.liquid
     pages use solenne-hours.js instead), and theme.liquid always sets
     body.dataset.palette = 'static' for those templates (index/product get 'home'/'product',
     but they never render this layout). PALETTES['static'] was always undefined, so `conf`
     was always undefined and every branch below that checked it never ran on a real page. */
  const clock = document.getElementById('navClock');

  /* stars */
  const stars = document.createElement('div');
  stars.className = 'stars';
  stars.setAttribute('aria-hidden', 'true');
  for (let i = 0; i < (isMobile ? 26 : 70); i++) {
    const s = document.createElement('i');
    const sz = Math.random() < 0.82 ? 1 : 2;
    s.style.cssText = `left:${(Math.random() * 100).toFixed(2)}%;top:${(Math.random() * 100).toFixed(2)}%;width:${sz}px;height:${sz}px;animation-delay:${(Math.random() * 5).toFixed(2)}s;animation-duration:${(3.5 + Math.random() * 4).toFixed(2)}s`;
    stars.appendChild(s);
  }
  document.body.appendChild(stars);

  // the one path that was ever reachable: the clock's static markup already reads
  // "03:00", so this is a no-op safety net rather than a real update.
  if (clock) clock.textContent = '03:00';

  /* ---------- drowsy type ---------- */
  document.querySelectorAll('[data-drowsy]').forEach(el => {
    if (!el.hasAttribute('aria-hidden')) el.setAttribute('aria-label', el.textContent.trim());
    let i = 0;
    (function walk(node) {
      [...node.childNodes].forEach(ch => {
        if (ch.nodeType === 3) {
          const frag = document.createDocumentFragment();
          for (const c of ch.textContent) {
            if (c.trim()) {
              const s = document.createElement('span');
              s.textContent = c;
              s.setAttribute('aria-hidden', 'true');
              s.style.setProperty('--i', i++);
              frag.appendChild(s);
            } else frag.appendChild(document.createTextNode(c));
          }
          ch.replaceWith(frag);
        } else if (ch.nodeType === 1) walk(ch);
      });
    })(el);
  });

  /* ---------- reveals ---------- */
  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('in-view'); io.unobserve(e.target); }
    });
    /* fire the moment any sliver enters, and pre-arm 12% below the fold so fast
       scrollers never race past an un-revealed (still-invisible) section */
  }, { threshold: 0, rootMargin: '0px 0px 12% 0px' });
  document.querySelectorAll('[data-reveal], [data-drowsy]').forEach(el => io.observe(el));

  /* ---------- video showcase: autoplay when in view, pause otherwise ---------- */
  const vsVids = document.querySelectorAll('.vs-vid[data-src]');
  if (vsVids.length) {
    const vidIO = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        const vid = e.target;
        if (e.isIntersecting) {
          if (!vid.src) { vid.src = vid.dataset.src; }
          vid.play().catch(() => {});
          vid.closest('.vs-video')?.classList.add('playing');
        } else {
          vid.pause();
          vid.closest('.vs-video')?.classList.remove('playing');
        }
      });
    }, { threshold: 0.25 });
    vsVids.forEach(v => vidIO.observe(v));
  }

  /* ---------- mobile menu ---------- */
  const mnav = document.getElementById('mnav');
  const menuBtn = document.getElementById('menuBtn');
  if (mnav && menuBtn) {
    const setMenu = (open) => { mnav.classList.toggle('open', open); menuBtn.setAttribute('aria-expanded', open); };
    menuBtn.addEventListener('click', () => setMenu(true));
    const closeBtn = document.getElementById('menuClose');
    if (closeBtn) closeBtn.addEventListener('click', () => setMenu(false));
    mnav.querySelectorAll('a').forEach(a => a.addEventListener('click', () => setMenu(false)));
  }

  /* ---------- magnetic buttons ---------- */
  if (fine && !reduced) {
    document.querySelectorAll('[data-mag]').forEach(btn => {
      btn.addEventListener('mousemove', e => {
        const r = btn.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        btn.style.transform = `translate(${dx * 0.18}px, ${dy * 0.3}px)`;
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transition = 'transform 0.6s cubic-bezier(0.16,1,0.3,1), background 0.35s, color 0.35s, border-color 0.35s';
        btn.style.transform = '';
        setTimeout(() => { btn.style.transition = ''; }, 600);
      });
    });
  }

  /* custom cursor removed 2026-07-16 — default system cursor on desktop */

  /* ================= PDP ================= */
  const cws = document.getElementById('cws');
  if (!cws) return;

  const productData = JSON.parse(document.getElementById('ProductData').textContent);
  const mediaMap = JSON.parse(document.getElementById('SolMediaMap').textContent);

  /* option indices: which option is Colour, which is Size */
  const optNames = (productData.options || []).map(o => (o.name || o).toString().toLowerCase());
  const colourIdx = Math.max(optNames.findIndex(n => /colou?r/.test(n)), 0);
  const sizeIdx = optNames.findIndex(n => /size/.test(n));

  const heroImg = document.getElementById('heroImg');
  const heroCap = document.getElementById('heroCap');
  const cwImg = document.getElementById('cwImg');
  const cwCap = document.getElementById('cwCap');
  const cwName = document.getElementById('cwName');
  const barMeta = document.getElementById('barMeta');
  const addBtn = document.getElementById('addBtn');
  const barAdd = document.getElementById('barAdd');

  let colour = cws.querySelector('.cw.on')?.dataset.name || 'Wine';
  let size = document.querySelector('.size.on')?.dataset.size || 'ii';

  function findVariant() {
    return (productData.variants || []).find(v => {
      const opts = v.options || [v.option1, v.option2, v.option3];
      const cOk = (opts[colourIdx] || '').toLowerCase() === colour.toLowerCase();
      const sOk = sizeIdx < 0 || (opts[sizeIdx] || '').toLowerCase() === size.toLowerCase();
      return cOk && sOk;
    });
  }
  let variant = findVariant();

  /* one / pair selector — "The pair" adds two; the $30 automatic discount lands at checkout */
  const pairPick = document.getElementById('pairPick');
  const buyQty = () => {
    const two = pairPick && pairPick.querySelector('input[value="2"]');
    return two && two.checked ? 2 : 1;
  };
  // shop currency is USD; only there can we honestly show the discounted pair total
  const buyPrice = (v) => {
    const q = buyQty();
    if (q === 2 && (window.theme && window.theme.moneyCurrency) === 'USD') return v.price * 2 - 3000;
    return v.price * q;
  };

  function syncBuyState() {
    variant = findVariant();
    const ok = variant && variant.available !== false;
    const qty = buyQty();
    const label = qty === 2 ? 'Add the pair' : 'Add to bag';
    // don't stomp a button that's mid add-to-bag (loading/done state)
    [addBtn, barAdd].forEach(b => { if (b && !b.dataset.busy) b.disabled = !ok; });
    // keep the accelerated-checkout form pointed at the live variant + quantity
    const vid = document.getElementById('buyVariantId');
    if (vid && variant) vid.value = variant.id;
    const qin = document.getElementById('buyQty');
    if (qin) qin.value = qty;
    if (addBtn && !addBtn.dataset.busy) {
      const t = addBtn.querySelector('.bb-text');
      const p = addBtn.querySelector('.bb-price');
      const s = addBtn.querySelector('.bb-sep');
      if (t) {
        t.textContent = ok ? label : 'Spoken for — restock soon';
        if (p) { p.textContent = ok ? money(buyPrice(variant)) : ''; p.style.display = ok ? '' : 'none'; }
        if (s) s.style.display = ok ? '' : 'none';
      } else {
        addBtn.textContent = ok ? `${label} — ${money(buyPrice(variant))}` : 'Spoken for — restock soon';
      }
    }
    if (barAdd && !barAdd.dataset.busy) barAdd.textContent = ok ? label + ' — ' + money(buyPrice(variant)) : 'Spoken for — restock soon';
    if (barMeta) barMeta.textContent = colour;
    // back-in-stock capture — surfaces only when the selected colour has no stock
    const restock = document.getElementById('restock');
    if (restock) {
      restock.hidden = !!ok;
      const tag = document.getElementById('restockTag');
      if (tag) tag.value = 'restock-' + colour.toLowerCase();
    }
  }

  /* Preload + decode the new colour FIRST, then hard-swap and re-run the
     film-develop (blur→focus). Fixes: (a) the .imgfade.loaded fill-animation
     used to override the .swapping fade so it never crossfaded; (b) a failed
     colour URL used to fall back to the Wine raw asset via the inline onerror —
     now a failed load simply keeps the current image. */
  function swapImg(el, src, alt) {
    if (!el || !src) return;
    const pre = new Image();
    pre.onload = () => {
      el.removeAttribute('srcset'); el.removeAttribute('sizes');
      el.onerror = null;                       // don't let the Wine fallback fire on a good swap
      el.src = src;
      if (alt) el.alt = alt;
      if (reduced) { el.classList.add('loaded'); return; }
      el.classList.remove('loaded');
      void el.offsetWidth;                     // reflow → restart the develop animation
      el.classList.add('loaded');              // the new colour "develops" in
    };
    pre.onerror = () => {};                     // colour failed to load: keep what's showing
    pre.src = src;
  }

  const docEl = document.documentElement;
  const cwWash = document.getElementById('cwWash');
  function riseCap(el, text) {
    if (!el) return;
    if (text != null) el.textContent = text;
    if (reduced) return;
    el.classList.remove('rise'); void el.offsetWidth; el.classList.add('rise');
  }
  /* Task 13: hyper-text scramble on colourway name change */
  function scrambleName(el, finalText) {
    if (!el || reduced) { el && (el.textContent = finalText); return; }
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    const rand = () => chars[Math.floor(Math.random() * chars.length)];
    let iter = 0; const total = 10;
    const id = setInterval(() => {
      el.textContent = finalText.split('').map((c, i) =>
        i < Math.floor((iter / total) * finalText.length) ? c : rand()
      ).join('');
      if (++iter > total) { el.textContent = finalText; clearInterval(id); }
    }, 28);
  }

  function setColour(key, name) {
    colour = name;
    if (cwName) scrambleName(cwName, name);
    let tint; let selectedDot;
    document.querySelectorAll('.cw').forEach(c => {
      const on = c.dataset.cw === key;
      c.classList.toggle('on', on);
      c.setAttribute('aria-pressed', on);
      if (on) { tint = c.dataset.tint; selectedDot = c; }
    });
    if (selectedDot && !reduced) {
      selectedDot.classList.remove('bloom');
      void selectedDot.offsetWidth;
      selectedDot.classList.add('bloom');
      selectedDot.addEventListener('animationend', () => selectedDot.classList.remove('bloom'), { once: true });
    }
    /* the room takes on the colour you choose */
    if (tint) {
      docEl.style.setProperty('--cw-tint', tint);
      if (cwWash && !reduced) { cwWash.classList.remove('lit'); void cwWash.offsetWidth; cwWash.classList.add('lit'); }
    }
    const hero = mediaMap.hero[key];
    if (hero) {
      swapImg(heroImg, hero.src, hero.alt);
      riseCap(heroCap, hero.onbody ? `${key} — on the body` : `${key} — same cut, every colour`);
    }
    if (mediaMap.cw[key]) {
      swapImg(cwImg, mediaMap.cw[key]);
      riseCap(cwCap, `${key} — as it hangs`);
    }
    syncBuyState();
    /* the 3D round (and anything else listening) follows the same choice */
    document.dispatchEvent(new CustomEvent('sol:colour', { detail: { key: key, name: name } }));
  }

  cws.addEventListener('click', e => {
    const b = e.target.closest('.cw');
    if (b) setColour(b.dataset.cw, b.dataset.name);
  });

  document.querySelectorAll('[data-pick]').forEach(item => {
    item.setAttribute('role', 'button');
    item.setAttribute('tabindex', '0');
    item.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); item.click(); }
    });
    item.addEventListener('click', () => {
      const key = item.dataset.pick;
      const name = key.charAt(0).toUpperCase() + key.slice(1);
      setColour(key, name);
      /* with the round live beside the grid the recolour happens in place —
         no scroll-away, no toast. Without it, fall back to showing the hero. */
      const stage = document.querySelector('#roundStage.live');
      if (stage) {
        stage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        cws.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.solToast(`${name} &mdash; <em>loading it now.</em>`);
      }
    });
  });

  /* sizes — one size now; picker removed from the DOM, so guard it */
  const sizesEl = document.getElementById('sizes');
  if (sizesEl) {
    sizesEl.addEventListener('click', e => {
      const b = e.target.closest('.size');
      if (!b) return;
      document.querySelectorAll('.size').forEach(s => { s.classList.remove('on'); s.setAttribute('aria-pressed', 'false'); });
      b.classList.add('on');
      b.setAttribute('aria-pressed', 'true');
      size = b.dataset.size;
      syncBuyState();
    });
  }

  syncBuyState();

  /* ---- mark sold-out colour swatches once at init ---- */
  (function markSoldOut() {
    const colIdx = Math.max(optNames.findIndex(n => /colou?r/.test(n)), 0);
    const hasStock = {};
    (productData.variants || []).forEach(v => {
      const opts = v.options || [v.option1, v.option2, v.option3];
      const c = (opts[colIdx] || '').toLowerCase();
      if (!(c in hasStock)) hasStock[c] = false;
      if (v.available !== false) hasStock[c] = true;
    });
    cws.querySelectorAll('.cw').forEach(btn => {
      const c = btn.dataset.cw;
      if (c && hasStock[c] === false) {
        btn.classList.add('sold-out');
        const lbl = btn.getAttribute('aria-label') || c;
        btn.setAttribute('aria-label', lbl + ' — sold out');
      }
    });
  })();

  /* ----- height → size recommender: parses 5'9" / 175cm / 70, picks the size ----- */
  (function fitRecommender() {
    const inp = document.getElementById('htIn');
    const out = document.getElementById('htOut');
    if (!inp || !out) return;
    const toInches = (v) => {
      const ft = v.match(/(\d)\s*['’]\s*(\d{1,2})?/);          // 5'9", 5’9
      if (ft) return (+ft[1]) * 12 + (+(ft[2] || 0));
      const n = parseFloat(v);
      if (isNaN(n)) return null;
      return n > 90 ? n / 2.54 : n;                             // cm → in, else assume inches
    };
    inp.addEventListener('input', () => {
      const inch = toInches(inp.value.trim());
      if (inch === null) { out.textContent = ''; return; }
      const s = inch <= 66 ? 'i' : inch <= 70 ? 'ii' : 'iii';   // i ≤5'6", ii 5'7"–5'10", iii ≥5'11"
      out.textContent = `size ${s} — your fit.`;
      const btn = document.querySelector(`.size[data-size="${s}"]`);
      if (btn && !btn.classList.contains('on')) btn.click();    // drives the real size handler
    });
  })();

  /* ----- deep-link a colour from the home colour rail (#wine, #rose, …) ----- */
  (function applyColourHash() {
    const key = (location.hash || '').slice(1).toLowerCase();
    if (!key) return;
    const btn = cws.querySelector(`.cw[data-cw="${key}"]`);
    if (btn) {
      setColour(key, btn.dataset.name);
      requestAnimationFrame(() => cws.scrollIntoView({ behavior: 'smooth', block: 'center' }));
    }
  })();

  /* ----- lazy-load the sherpa motion loop only when it nears the viewport ----- */
  const motion = document.querySelector('.gallery video[data-src]');
  if (motion) {
    new IntersectionObserver((entries, ob) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        motion.src = motion.dataset.src;
        motion.removeAttribute('data-src');
        if (!reduced) motion.play?.().catch(() => {});
        ob.disconnect();
      });
    }, { rootMargin: '300px 0px' }).observe(motion);
  }

  /* ----- size guide modal ----- */
  const SG_DATA = {
    in: { A: '56"', B: '66"', C: '30"', D: '53"', E: '44"' },
    cm: { A: '142', B: '168', C: '76', D: '135', E: '112' },
  };
  let sgUnit = 'in';
  const sgOverlay = document.getElementById('sgOverlay');
  if (sgOverlay) {
    function sgRender() {
      const d = SG_DATA[sgUnit];
      ['A','B','C','D','E'].forEach(k => {
        const el = document.getElementById('sg-' + k);
        if (!el) return;
        el.classList.add('fade');
        setTimeout(() => {
          el.textContent = sgUnit === 'cm' ? d[k] + ' cm' : d[k];
          el.classList.remove('fade');
        }, 180);
      });
    }
    function sgClose() {
      sgOverlay.classList.remove('open');
      document.body.style.overflow = '';
    }
    document.getElementById('sgClose').addEventListener('click', sgClose);
    sgOverlay.addEventListener('click', e => { if (e.target === sgOverlay) sgClose(); });
    addEventListener('keydown', e => { if (e.key === 'Escape' && sgOverlay.classList.contains('open')) sgClose(); });
    /* keep Tab inside the modal while it's open */
    sgOverlay.addEventListener('keydown', e => {
      if (e.key !== 'Tab' || !sgOverlay.classList.contains('open')) return;
      const f = sgOverlay.querySelectorAll('button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    sgOverlay.querySelectorAll('.sg-u').forEach(btn => {
      btn.addEventListener('click', () => {
        sgUnit = btn.dataset.u;
        sgOverlay.querySelectorAll('.sg-u').forEach(b => b.classList.toggle('on', b === btn));
        sgRender();
      });
    });
    const openSG = () => {
      sgUnit = 'in';
      sgOverlay.querySelectorAll('.sg-u').forEach(b => b.classList.toggle('on', b.dataset.u === 'in'));
      sgRender();
      sgOverlay.classList.add('open');
      document.body.style.overflow = 'hidden';
      document.getElementById('sgClose').focus();
    };
    ['sizeGuide', 'sizeGuideAcc'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.addEventListener('click', openSG);
    });
  }

  /* ----- Task 11: ripple on add-to-bag mousedown ----- */
  function fireRipple(btn, e) {
    if (reduced) return;
    const r = btn.getBoundingClientRect();
    const size = Math.max(r.width, r.height);
    const x = (e.clientX - r.left) - size / 2;
    const y = (e.clientY - r.top) - size / 2;
    const rip = document.createElement('span');
    rip.className = 'ripple';
    rip.style.cssText = `width:${size}px;height:${size}px;left:${x}px;top:${y}px`;
    btn.appendChild(rip);
    setTimeout(() => rip.remove(), 700);
  }

  /* ----- Task 12: confetti burst on cart success ----- */
  function solConfetti(originEl) {
    if (reduced || !originEl) return;
    const colors = ['#F4F1EA','#E8E2D5','#C9C2B2','#1E2A4A','#aab3c9'];
    const r = originEl.getBoundingClientRect();
    const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    const cvs = document.createElement('canvas');
    cvs.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:9999';
    cvs.width = innerWidth; cvs.height = innerHeight;
    document.body.appendChild(cvs);
    const ctx2 = cvs.getContext('2d');
    const particles = Array.from({length: 28}, () => ({
      x: cx, y: cy,
      vx: (Math.random() - 0.5) * 9,
      vy: Math.random() * -10 - 4,
      w: Math.random() * 7 + 4, h: Math.random() * 3 + 2,
      rot: Math.random() * 360, rotV: (Math.random() - 0.5) * 8,
      color: colors[Math.floor(Math.random() * colors.length)],
      life: 1
    }));
    let frame;
    function draw() {
      ctx2.clearRect(0, 0, cvs.width, cvs.height);
      let alive = false;
      particles.forEach(p => {
        p.x += p.vx; p.vy += 0.45; p.y += p.vy;
        p.rot += p.rotV; p.life -= 0.018;
        if (p.life <= 0) return; alive = true;
        ctx2.save();
        ctx2.globalAlpha = p.life;
        ctx2.translate(p.x, p.y);
        ctx2.rotate(p.rot * Math.PI / 180);
        ctx2.fillStyle = p.color;
        ctx2.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx2.restore();
      });
      if (alive) frame = requestAnimationFrame(draw);
      else cvs.remove();
    }
    frame = requestAnimationFrame(draw);
    setTimeout(() => { cancelAnimationFrame(frame); cvs.remove(); }, 2200);
  }

  /* ----- add to bag with loading → success states (SolenneBag from solenne-bag.js) ----- */
  function addToBag(btn) {
    if (!variant || (btn && btn.dataset.busy)) return;
    const isMain = btn === addBtn;
    if (btn) {
      btn.dataset.busy = '1';
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      if (isMain) btn.classList.add('is-loading');
      else { btn.dataset.idle = btn.textContent; btn.textContent = 'adding…'; }
    }
    const done = (ok) => {
      if (!btn) return;
      if (isMain) btn.classList.remove('is-loading');
      if (ok) {
        if (isMain) btn.classList.add('is-done');
        else btn.textContent = 'in your bag';
        if (window.SolenneBag) window.SolenneBag.open();
        solConfetti(isMain ? addBtn : btn);
        if (window.fbq && variant) window.fbq('track','AddToCart',{content_type:'product_group',content_ids:[String(productData.id)],content_name:productData.title,value:variant.price*buyQty()/100,currency:window.theme.moneyCurrency||'USD'});
      }
      setTimeout(() => {
        if (isMain) btn.classList.remove('is-done');
        btn.removeAttribute('aria-busy');
        delete btn.dataset.busy;
        btn.disabled = false;
        syncBuyState();              // restores live label/price/availability
      }, ok ? 1700 : 0);
    };
    Promise.resolve(window.SolenneBag.add(variant.id, btn, buyQty()))
      .then(() => done(true))
      .catch(() => done(false));
  }
  if (addBtn) addBtn.addEventListener('click', e => { e.preventDefault(); addToBag(e.currentTarget); });
  if (addBtn) addBtn.addEventListener('mousedown', e => fireRipple(addBtn, e));
  if (barAdd) barAdd.addEventListener('click', e => addToBag(e.currentTarget));
  if (pairPick) pairPick.addEventListener('change', () => {
    // .on mirrors :checked for browsers without :has() support
    pairPick.querySelectorAll('.oq').forEach(l => l.classList.toggle('on', l.querySelector('input').checked));
    syncBuyState();
  });

  /* ----- sticky buy bar ----- */
  const buybar = document.getElementById('buybar');
  if (buybar && addBtn) {
    new IntersectionObserver(([e]) => {
      // phones: bar shows whenever the buy button is off-screen (it starts below the fold);
      // desktop: only after scrolling past the buy module, as before
      const offAbove = e.boundingClientRect.top < 0;
      buybar.classList.toggle('show', !e.isIntersecting && (innerWidth < 961 || offAbove));
    }, { threshold: 0 }).observe(addBtn);
  }
})();

/* ===== mobile PDP gallery → swipe carousel nav (dots + counter) =====
   The gallery is a long vertical stack on desktop; on phones CSS turns it into a
   horizontal scroll-snap carousel. This adds the dot/counter affordance so it's
   obviously swipeable. No-ops on desktop and if there's nothing to page through.
   Rebuilds itself when the 3D slide lands so the dots include it. */
(function () {
  var io = null;

  function build() {
    var gallery = document.getElementById('gallery');
    if (!gallery) return;
    if (!window.matchMedia('(max-width: 760px)').matches) return;

    if (io) { io.disconnect(); io = null; }
    var stale = document.querySelector('.gallery-nav');
    if (stale) stale.remove();

    var slides = Array.prototype.slice.call(
      gallery.querySelectorAll(':scope > figure, :scope > .gallery-duo > figure')
    );
    if (slides.length < 2) return;

    var nav = document.createElement('div');
    nav.className = 'gallery-nav';
    nav.setAttribute('aria-hidden', 'true');

    var dots = slides.map(function (s, idx) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'gn-dot';
      b.setAttribute('aria-label', 'Go to image ' + (idx + 1));
      b.addEventListener('click', function () {
        s.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      });
      nav.appendChild(b);
      return b;
    });

    var count = document.createElement('span');
    count.className = 'gn-count';
    count.textContent = '1 / ' + slides.length;
    nav.appendChild(count);
    gallery.insertAdjacentElement('afterend', nav);

    var active = -1;
    function setActive(i) {
      if (i === active) return;
      active = i;
      dots.forEach(function (d, k) { d.classList.toggle('on', k === i); });
      count.textContent = (i + 1) + ' / ' + slides.length;
    }
    setActive(0);

    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            var i = slides.indexOf(e.target);
            if (i > -1) setActive(i);
          }
        });
      }, { root: gallery, threshold: 0.6 });
      slides.forEach(function (s) { io.observe(s); });
    }
  }

  build();
  document.addEventListener('sol3d:exhale-ready', build);
})();

/* ===== 360 on demand =====
   Capable desktops auto-load the WebGL cloak from theme.liquid (SOL3D.auto);
   everything else downloads none of it until asked. This reveals the round
   column in the colour section whenever the device could show the mesh, and
   wires the two "see it in the round" buttons (gallery + round column) to
   fetch on tap where it didn't auto-load. */
(function () {
  var btns = ['spin360', 'round360']
    .map(function (id) { return document.getElementById(id); })
    .filter(Boolean);
  if (!btns.length) return;
  var labelOf = function (btn) { return btn.querySelector('.g360-label') || btn; };

  /* ripple: a translucent ink bloom grows from the tap point and fades — ported from
     Magic UI's ripple-button. overflow:hidden on the pill clips it to shape; the label
     sits above via z-index so status-text swaps below never wipe it. */
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    var RIPPLE_MS = 600;                                        // Magic UI default duration
    btns.forEach(function (btn) {
      btn.addEventListener('pointerdown', function (e) {
        var rect = btn.getBoundingClientRect();
        var size = Math.max(rect.width, rect.height);
        var ripple = document.createElement('span');
        ripple.className = 'g360-ripple';
        ripple.style.width = ripple.style.height = size + 'px';
        ripple.style.left = (e.clientX - rect.left - size / 2) + 'px';
        ripple.style.top  = (e.clientY - rect.top  - size / 2) + 'px';
        ripple.style.setProperty('--dur', RIPPLE_MS + 'ms');
        btn.appendChild(ripple);
        setTimeout(function () { ripple.remove(); }, RIPPLE_MS);
      });
    });
  }

  if (!(window.SOL3D && window.SOL3D.load)) return;             // no WebGL / import maps
  var duo = document.querySelector('.spec-duo');
  if (duo) duo.classList.add('has-round');                      // reveal the round column
  document.addEventListener('sol3d:exhale-error', function () {
    /* mesh unavailable — fold the column away, the grid stands full-width */
    if (duo) duo.classList.remove('has-round');
  });
  if (window.SOL3D.auto) return;                                // desktop loads itself

  btns.forEach(function (btn) {
    btn.hidden = false;
    btn.addEventListener('click', function () {
      btns.forEach(function (b) { b.disabled = true; labelOf(b).textContent = 'loading the model…'; });
      window.SOL3D.load(['exhale']);
    });
  });
  document.addEventListener('sol3d:exhale-ready', function (e) {
    btns.forEach(function (b) { b.remove(); });
    var fig = e.detail && e.detail.fig;
    if (fig) fig.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
  document.addEventListener('sol3d:exhale-error', function () {
    btns.forEach(function (b) { labelOf(b).textContent = 'the model didn’t load — the photographs stand'; });
  });
})();

/* magic-card: cursor spotlight on guarantee items (desktop / fine-pointer only) */
(function () {
  if (!window.matchMedia('(pointer: fine)').matches) return;
  document.querySelectorAll('.guarantee-item').forEach(function (card) {
    card.addEventListener('mousemove', function (e) {
      var r = card.getBoundingClientRect();
      card.style.setProperty('--gx', (e.clientX - r.left) + 'px');
      card.style.setProperty('--gy', (e.clientY - r.top) + 'px');
    });
  });
})();


/* ===== nav: transparent over the mobile home hero, solid once you scroll =====
   Class only — the visual change is scoped to home + <=960px in CSS, so desktop
   and the PDP are untouched. rAF-throttled, passive. */
(function () {
  var nav = document.querySelector('.nav');
  if (!nav || document.body.dataset.palette !== 'home') return;
  var ticking = false;
  function set() { nav.classList.toggle('nav--top', scrollY < 60); ticking = false; }
  addEventListener('scroll', function () {
    if (!ticking) { ticking = true; requestAnimationFrame(set); }
  }, { passive: true });
  set();
})();


/* ===== pixel-image reveal on the campaign shot (ported from Magic UI pixel-image) =====
   When the "worn anywhere" figure scrolls into view, it assembles from a 6×4 grid of tiles
   that fade in on a random stagger and warm from grayscale to colour, then the overlay
   dissolves to the crisp <img> beneath. Tiles reuse the frame the browser already picked
   from srcset (currentSrc), so there is no second download. Reduced-motion skips it, and
   the real image is always present for no-JS / SEO. */
(function () {
  var figs = document.querySelectorAll('figure.pixel-reveal');
  if (!figs.length) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var COLS = 6, ROWS = 4;     // Magic UI default 6x4 grid
  var FADE = 1000;            // pixelFadeInDuration (ms) — matches the CSS tile transition
  var MAX_DELAY = 1200;       // maxAnimationDelay (ms)
  var COLOR_DELAY = 1300;     // colorRevealDelay (ms)

  function build(fig) {
    if (fig.dataset.pixelDone) return;
    fig.dataset.pixelDone = '1';
    var img = fig.querySelector('img');
    var src = img && (img.currentSrc || img.src);
    if (!src) { fig.removeAttribute('data-px'); return; }   // can't build — let the real image show

    var overlay = document.createElement('div');
    overlay.className = 'pixel-overlay';
    var maxDelay = 0;

    for (var r = 0; r < ROWS; r++) {
      for (var c = 0; c < COLS; c++) {
        var tile = document.createElement('span');
        tile.className = 'pixel-tile';
        tile.style.left   = (c * 100 / COLS) + '%';
        tile.style.top    = (r * 100 / ROWS) + '%';
        tile.style.width  = (100 / COLS) + '%';
        tile.style.height = (100 / ROWS) + '%';
        tile.style.backgroundImage = 'url("' + src + '")';
        tile.style.backgroundSize  = (COLS * 100) + '% ' + (ROWS * 100) + '%';
        tile.style.backgroundPosition =
          (COLS > 1 ? c * 100 / (COLS - 1) : 0) + '% ' +
          (ROWS > 1 ? r * 100 / (ROWS - 1) : 0) + '%';
        var delay = Math.random() * MAX_DELAY;
        if (delay > maxDelay) maxDelay = delay;
        tile.style.transitionDelay = delay + 'ms';
        overlay.appendChild(tile);
      }
    }
    fig.appendChild(overlay);

    requestAnimationFrame(function () {                 // let the tiles paint at opacity 0…
      requestAnimationFrame(function () { overlay.classList.add('lit'); });   // …then bloom in
    });
    setTimeout(function () { overlay.classList.add('warm'); }, COLOR_DELAY);  // grayscale → colour
    setTimeout(function () {                                                  // cross-fade to the crisp img
      fig.setAttribute('data-px', 'done');                                   // reveal the real image beneath…
      overlay.classList.add('gone');                                         // …as the assembled tiles dissolve
      setTimeout(function () { overlay.remove(); }, 650);
    }, Math.max(maxDelay + FADE + 250, COLOR_DELAY + FADE));
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      var fig = e.target, img = fig.querySelector('img');
      if (!img) return;
      if (img.complete && (img.currentSrc || img.naturalWidth)) build(fig);
      else img.addEventListener('load', function () { build(fig); }, { once: true });
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
  // hide the real image up front (JS-only, so no-JS still shows it) so it never flashes
  // crisp before the tiles assemble; it cross-fades back in once the overlay is done.
  figs.forEach(function (f) { f.setAttribute('data-px', 'pre'); io.observe(f); });
})();


/* ===== number ticker — the stats count up as they enter view (ported from Magic UI
   number-ticker). easeOutCubic, ~1.1s; reduced-motion drops straight to the final figure.
   Locale-formatted so larger counts get their comma. Markup already carries data-count. */
(function () {
  var els = document.querySelectorAll('[data-count]');
  if (!els.length) return;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fmt = function (n) { return Math.round(n).toLocaleString(); };

  function run(el) {
    var target = parseFloat(el.getAttribute('data-count'));
    if (isNaN(target)) return;
    if (reduce) { el.textContent = fmt(target); return; }
    var DUR = 1100, t0 = 0;
    (function step(now) {
      if (!t0) t0 = now;
      var p = Math.min((now - t0) / DUR, 1);
      el.textContent = fmt(target * (1 - Math.pow(1 - p, 3)));   // easeOutCubic
      if (p < 1) requestAnimationFrame(step);
      else el.textContent = fmt(target);
    })(0);
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { if (e.isIntersecting) { io.unobserve(e.target); run(e.target); } });
  }, { threshold: 0.6 });
  els.forEach(function (el) {
    if (!reduce) el.textContent = '0';
    io.observe(el);
  });
})();


/* ===== headline word-reveal — each word de-blurs and rises on a gentle stagger as the
   line enters view (ported from Magic UI text-reveal / text-animate). Inline <em>/<span>
   emphasis is preserved by recursing rather than flattening. Reduced-motion and no-JS
   leave the full headline untouched; a backstop guarantees nothing stays hidden. */
(function () {
  var heads = document.querySelectorAll('[data-words]');
  if (!heads.length) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function wrap(node) {
    Array.prototype.slice.call(node.childNodes).forEach(function (child) {
      if (child.nodeType === 3) {                         // text node → split into word spans
        var frag = document.createDocumentFragment();
        child.textContent.split(/(\s+)/).forEach(function (part) {
          if (part === '') return;
          if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
          var s = document.createElement('span');
          s.className = 'rw';
          s.textContent = part;
          frag.appendChild(s);
        });
        node.replaceChild(frag, child);
      } else if (child.nodeType === 1) {                  // element (em, span…) → keep, recurse
        wrap(child);
      }
    });
  }

  var STEP = 80, CAP = 16;                                // ms per word, capped so long lines don't drag
  heads.forEach(function (h) {
    wrap(h);
    Array.prototype.forEach.call(h.querySelectorAll('.rw'), function (s, i) {
      s.style.transitionDelay = (Math.min(i, CAP) * STEP) + 'ms';
    });
  });

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { if (e.isIntersecting) { io.unobserve(e.target); e.target.classList.add('rw-in'); } });
  }, { threshold: 0.25, rootMargin: '0px 0px -6% 0px' });
  heads.forEach(function (h) { io.observe(h); });

  setTimeout(function () {                                // backstop: never strand a hidden headline
    heads.forEach(function (h) { h.classList.add('rw-in'); });
  }, 4000);
})();


/* ===== spinning text — letters laid around a ring that rotates slowly (ported from Magic
   UI spinning-text). Radius is derived from letter count so the phrase spaces itself evenly
   around the circle. The visible copy is decorative; the real guarantee is stated in text. */
(function () {
  var seals = document.querySelectorAll('[data-spin]');
  if (!seals.length) return;
  seals.forEach(function (seal) {
    var chars = seal.getAttribute('data-spin').split('');
    var total = chars.length;
    var radius = total / (2 * Math.PI);                  // ch — even spacing around the ring
    var ring = document.createElement('div');
    ring.className = 'ss-ring';
    chars.forEach(function (ch, i) {
      var s = document.createElement('span');
      s.className = 'ss-ch';
      s.textContent = ch;
      s.style.transform = 'translate(-50%,-50%) rotate(' + (360 / total * i) + 'deg) translateY(-' + radius + 'ch)';
      ring.appendChild(s);
    });
    seal.insertBefore(ring, seal.firstChild);
  });
})();


/* ===== word rotate — one word swaps on a vertical roll (ported from Magic UI word-rotate).
   Words are pipe-separated in data-rotate; the slot clips the roll. Reduced-motion pins the
   first word. Placed word-final in its line so widths never shove the surrounding copy. */
(function () {
  var els = document.querySelectorAll('[data-rotate]');
  if (!els.length) return;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  els.forEach(function (el) {
    var words = el.getAttribute('data-rotate').split('|').filter(Boolean);
    if (words.length < 2) return;
    var inner = document.createElement('span');
    inner.className = 'wrot-in';
    inner.textContent = (el.textContent || '').trim() || words[0];
    el.textContent = '';
    el.appendChild(inner);
    if (reduce) return;
    var i = 0, EASE = 'transform .22s var(--ease-out), opacity .22s var(--ease-out)';
    setInterval(function () {
      inner.style.transition = EASE;
      inner.style.transform = 'translateY(-1.4em)';       // exaggerated throw
      inner.style.opacity = '0';
      setTimeout(function () {
        i = (i + 1) % words.length;
        inner.textContent = words[i];
        inner.style.transition = 'none';
        inner.style.transform = 'translateY(1.4em)';
        void inner.offsetHeight;                          // reflow so the entry animates
        inner.style.transition = EASE;
        inner.style.transform = 'translateY(0)';
        inner.style.opacity = '1';
      }, 220);
    }, 1500);                                             // faster cadence
  });
})();


/* ===== hyper text — characters scramble, then lock in left→right when the readout enters
   view (ported from Magic UI hyper-text). Separators (space, colon, middot) stay fixed;
   data-hyper-set narrows the scramble alphabet (digits for the clock). */
(function () {
  var els = document.querySelectorAll('[data-hyper]');
  if (!els.length) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function scramble(el) {
    var target = el.getAttribute('data-hyper');
    var chars = target.split('');
    var set = (el.getAttribute('data-hyper-set') || 'ABCDEFGHIJKLMNOPQRSTUVWXYZ').split('');
    var DUR = 900, t0 = 0;
    (function frame(now) {
      if (!t0) t0 = now;
      var p = Math.min((now - t0) / DUR, 1), locked = p * chars.length;
      el.textContent = chars.map(function (c, idx) {
        if (c === ' ' || c === ':' || c === '·') return c;
        return idx <= locked ? c : set[Math.floor(Math.random() * set.length)];
      }).join('');
      if (p < 1) requestAnimationFrame(frame);
      else el.textContent = target;
    })(0);
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { if (e.isIntersecting) { io.unobserve(e.target); scramble(e.target); } });
  }, { threshold: 0.5 });
  els.forEach(function (el) { io.observe(el); });
})();


/* ===== read-reveal — body copy brightens word-by-word as it scrolls up into the reading
   zone, so a wall of description develops as you read it (ported from Magic UI text-reveal,
   driven off each block's viewport position rather than a 200vh pin so it doesn't distort the
   page). Inline <strong>/<span> emphasis is preserved. Only blocks near the viewport are
   computed, on a rAF-throttled scroll. Reduced-motion / no-JS leave the copy fully legible. */
(function () {
  var blocks = document.querySelectorAll('[data-read]');
  if (!blocks.length) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var BASE = 0.16;                                         // resting opacity of an un-lit word (low = stronger reveal)
  var TOP = 0.85, BOTTOM = 0.5;                            // viewport fractions the reveal spans

  function splitWords(node, out) {
    Array.prototype.slice.call(node.childNodes).forEach(function (child) {
      if (child.nodeType === 3) {
        var frag = document.createDocumentFragment();
        child.textContent.split(/(\s+)/).forEach(function (part) {
          if (part === '') return;
          if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
          var s = document.createElement('span');
          s.className = 'read-w';
          s.textContent = part;
          s.style.opacity = BASE;
          frag.appendChild(s);
          out.push(s);
        });
        node.replaceChild(frag, child);
      } else if (child.nodeType === 1) {
        splitWords(child, out);
      }
    });
  }

  var items = [];
  Array.prototype.forEach.call(blocks, function (block) {
    var words = [];
    splitWords(block, words);
    if (!words.length) return;
    var it = { el: block, words: words, active: false };
    block.__readItem = it;
    items.push(it);
  });

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { if (e.target.__readItem) e.target.__readItem.active = e.isIntersecting; });
    schedule();
  }, { rootMargin: '25% 0px 25% 0px' });                  // wake blocks a little before they enter
  items.forEach(function (it) { io.observe(it.el); });

  var ticking = false;
  function schedule() { if (!ticking) { ticking = true; requestAnimationFrame(tick); } }
  function tick() {
    ticking = false;
    var vh = window.innerHeight;
    var span = (TOP - BOTTOM) * vh;
    items.forEach(function (it) {
      if (!it.active) return;
      var r = it.el.getBoundingClientRect();
      var p = (TOP * vh - r.top) / (span + r.height);       // 0 entering from below → 1 once read
      p = p < 0 ? 0 : p > 1 ? 1 : p;
      var lit = p * it.words.length;
      for (var i = 0; i < it.words.length; i++) {
        var o = lit - i; o = o < 0 ? 0 : o > 1 ? 1 : o;     // 1-word feather at the leading edge
        it.words[i].style.opacity = (BASE + (1 - BASE) * o).toFixed(3);
      }
    });
  }
  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', schedule, { passive: true });
  tick();
})();
